type AnyRecord = Record<string, any>;

const isAskQuestionNode = (node: AnyRecord | null | undefined) => {
  return Boolean(node && (node as any).type === 'AskQuestion');
};

/**
 * Normalize AskQuestion node data coming from API/back-end into UI-friendly keys.
 *
 * Back-end payload uses `question.options`, while UI uses:
 * - Multiple choice: `question.multipleOptions`
 * - WhatsApp list: `question.whatsappListItems`
 */
export function normalizeAskQuestionForUI<T extends AnyRecord>(node: T): T {
  if (!isAskQuestionNode(node)) {
    return node;
  }

  const data: AnyRecord = (node as any).data || {};
  const question: AnyRecord = data.question || {};
  const questionType = question.type;
  const optionsFromApi = question.options;

  if (questionType === 'options') {
    if (!Array.isArray(question.multipleOptions) && Array.isArray(optionsFromApi)) {
      question.multipleOptions = optionsFromApi;
    }
  } else if (questionType === 'whatsapp_list') {
    if (!Array.isArray(question.whatsappListItems) && Array.isArray(optionsFromApi)) {
      question.whatsappListItems = optionsFromApi;
    }
  }

  return {
    ...node,
    data: {
      ...data,
      question,
    },
  } as T;
}

/**
 * Normalize AskQuestion node data for API/export payload.
 *
 * UI stores different shapes under different keys, but back-end expects `question.options`.
 */
export function normalizeAskQuestionForApi<T extends AnyRecord>(node: T): T {
  if (!isAskQuestionNode(node)) {
    return node;
  }

  const data: AnyRecord = (node as any).data || {};
  const question: AnyRecord = data.question || {};
  const questionType = question.type;

  if (questionType === 'options') {
    return {
      ...node,
      data: {
        ...data,
        question: {
          ...question,
          options: Array.isArray(question.multipleOptions) ? question.multipleOptions : [],
        },
      },
    } as T;
  }

  if (questionType === 'whatsapp_list') {
    return {
      ...node,
      data: {
        ...data,
        question: {
          ...question,
          options: Array.isArray(question.whatsappListItems) ? question.whatsappListItems : [],
        },
      },
    } as T;
  }

  return node;
}
